import React, { useEffect, useState } from 'react';
import { DataGrid, EditModal, NGODatePicker } from '../../../common';
import editIcon2 from "../../../assets/edit2.svg";
import closeIcon from '../../../images/icons/Icon2.png';
import { DFPAPIEndpointGalley } from '../../../utils/axios';
import { useTranslation } from 'react-i18next';
import { ToastContainer, toast } from 'react-toastify';
import { debounce, formatDateToYYYYMMDD } from '../../../utils/commonFunctions';
import usePermissions from '../../../utils/userPermissions';

const Columns = [
  { 
    field: 'key', 
    headerName: 'Key', 
    sortable: true, 
    filter: true,
    flex: 1,
    minWidth: 110,
    resizable: true,
    cellClass: 'ag-cell-center',
    headerClass: 'ag-header-cell-center',
    cellStyle: { textAlign: 'center', justifyContent: 'center', display: 'flex', alignItems: 'center' }
  },
  { 
    field: 'date', 
    headerName: 'Date', 
    sortable: true, 
    filter: true,
    flex: 1,
    minWidth: 130,
    resizable: true,
    cellClass: 'ag-cell-center',
    headerClass: 'ag-header-cell-center',
    cellStyle: { textAlign: 'center', justifyContent: 'center', display: 'flex', alignItems: 'center' },
    valueFormatter: params => params.value ? String(params.value).split('T')[0].split("-").reverse().join("/") : '',
    comparator: (dateA, dateB) => new Date(dateA || 0) - new Date(dateB || 0)
  },
  { 
    field: 'warehouse', 
    headerName: 'Warehouse', 
    sortable: true, 
    filter: true,
    flex: 1,
    minWidth: 130,
    resizable: true,
    cellClass: 'ag-cell-center',
    headerClass: 'ag-header-cell-center',
    cellStyle: { textAlign: 'center', justifyContent: 'center', display: 'flex', alignItems: 'center' }
  },
  { 
    field: 'caterer', 
    headerName: 'Caterer', 
    sortable: true, 
    filter: true,
    flex: 1,
    minWidth: 130,
    resizable: true,
    cellClass: 'ag-cell-center',
    headerClass: 'ag-header-cell-center',
    cellStyle: { textAlign: 'center', justifyContent: 'center', display: 'flex', alignItems: 'center' }
  },
  { 
    field: 'menuid', 
    headerName: 'Menu ID', 
    sortable: true, 
    filter: true,
    flex: 1,
    minWidth: 110,
    resizable: true,
    cellClass: 'ag-cell-center',
    headerClass: 'ag-header-cell-center',
    cellStyle: { textAlign: 'center', justifyContent: 'center', display: 'flex', alignItems: 'center' },
    comparator: (numA, numB) => (Number(numA) || 0) - (Number(numB) || 0)
  },
];

const keyArr = ["DELT", "DELA", "DELC", "BOMT", "BOMA", "BOMC", "MAAT", "MAAC", "BLRT", "CCUT", "HYDC", "COKC", "PNQC", "GOIT", "GOXT", "AMDC", "TRVU", "IXEO", "ATQT"];
const wHArry = ["DEL", "DEL", "DEL", "BOM", "BOM", "BOM", "MAA", "MAA", "BLR", "CCU", "HYD", "COK", "PNQ", "GOI", "GOX", "AMD", "TRV", "IXE", "ATQ"];
const catererArr = ["TAJ", "ASC", "CFC", "TAJ", "ASC", "CFC", "TAJ", "CAFS", "TAJ", "TAJ", "CAFS", "CAFS", "CAFS", "TAJ", "TAJ", "CAFS", "USK", "OPRL", "TAJ"];

const actionColumnProperties = {
  pinned: "right",
  minWidth: 80,
  width: 120,
  autoHeight: true,
  tooltipField: "Edit",
};

export const MealRotation = () => {
  const [actionItems] = useState([{
    title: "Actions",
    attribute: "edit",
  }]);
  const { ready } = useTranslation();
  const [filters, setFilters] = useState({});
  const [reloadCounter, setReloadCounter] = useState(1);
  const [createMenuRotation, setCreateMenuRotation] = useState(false);
  const [rotationCreateDates, setRotationCreateDates] = useState([]);
  const [rotationData, setRotationData] = useState({
    mealNumber: 4,
    repeat: 3
  });
  const [matrixData, setMatrixData] = useState({}); // { [`${date}_${key}`]: number }
  const [rDataExist, setRDataExist] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Edit Modal state
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editForm, setEditForm] = useState({
    id: '',
    key: '',
    date: '',
    warehouse: '',
    caterer: '',
    menuid: ''
  });
  const [isEditSubmitting, setIsEditSubmitting] = useState(false);

  const { hasPermission: permCreate } = usePermissions('create', "configuration.fsc.meal-rotation");
  const { hasPermission: permEdit } = usePermissions('edit', "configuration.fsc.meal-rotation");
  const createMealRotation = permCreate !== false;
  const editMealRotation = permEdit !== false;

  useEffect(() => {
    const today = new Date();
    const oneMonthAhead = new Date(today);
    oneMonthAhead.setMonth(oneMonthAhead.getMonth() + 1);

    const datesArray = getDates(today, oneMonthAhead);
    setRotationCreateDates([...datesArray]);
  }, []);

  function getDates(startDate, endDate) {
    const dates = [];
    let currentDate = new Date(startDate);

    while (currentDate <= endDate) {
      const year = currentDate.getFullYear();
      const month = String(currentDate.getMonth() + 1).padStart(2, '0');
      const day = String(currentDate.getDate()).padStart(2, '0');
      dates.push(`${year}-${month}-${day}`);
      currentDate.setDate(currentDate.getDate() + 1);
    }
    return dates;
  }

  async function fetchMealRotationData(page = 1, limit = 50, searchParam = '') {
    try {
      const queryParams = { ...filters };
      if (searchParam && typeof searchParam === 'string' && searchParam.trim()) {
        queryParams.key = searchParam.trim();
      }
      queryParams._t = Date.now();
      const filterParams = objectToUrlParams(queryParams);
      const resp = await DFPAPIEndpointGalley.get("galley/ai/menu-rotation/configs?" + filterParams);
      if (resp && (resp.status === 1 || resp.success)) {
        const menuRotationPolicy = resp?.data?.menuRotationPolicy || resp?.menuRotationPolicy || resp?.result || [];
        const formatted = menuRotationPolicy.map(data => {
          return {
            id: data.id || data._id,
            key: data.key,
            date: data.date,
            warehouse: typeof data.warehouse === 'object' ? data.warehouse?.name || '' : (data.warehouse || ''),
            caterer: typeof data.caterer === 'object' ? data.caterer?.name || '' : (data.caterer || ''),
            menuid: data.menuId || data.menuid || '',
            rawRecord: data
          };
        });
        return {
          count: formatted.length,
          items: [...formatted]
        };
      }
      return { count: 0, items: [] };
    } catch (error) {
      console.error("Error fetching menu rotation data:", error);
      return { count: 0, items: [] };
    }
  }

  const getActionCellIcons = (cellDef, data) => {
    if (cellDef.attribute === "edit" && editMealRotation) {
      return [{ src: editIcon2, value: "edit", title: "Edit" }];
    }
    return [];
  };

  const onAction = (e, action, selectedRowData) => {
    if (action?.value === "edit" || action?.attribute === "edit") {
      const recordId = selectedRowData?.id || selectedRowData?.rawRecord?._id || selectedRowData?.rawRecord?.id || selectedRowData?._id;
      let formattedDate = selectedRowData?.date ? String(selectedRowData.date).split('T')[0] : '';

      setEditForm({
        id: recordId,
        key: selectedRowData?.key || '',
        date: formattedDate,
        warehouse: selectedRowData?.warehouse || '',
        caterer: selectedRowData?.caterer || '',
        menuid: selectedRowData?.menuid || selectedRowData?.menuId || ''
      });
      setEditModalOpen(true);
    }
  };

  const updateFilterState = (name, value) => {
    setFilters(prev => {
      const copy = { ...prev };
      if (value !== undefined && value !== null && String(value).trim() !== '') {
        copy[name] = value;
      } else {
        delete copy[name];
      }
      return copy;
    });
    setReloadCounter(c => c + 1);
  };

  const debouncedUpdateFilter = debounce((name, val) => {
    updateFilterState(name, val);
  }, 300);

  const filterChange = (e) => {
    if (!e) return;
    let name = '';
    let value = '';

    if (e.target) {
      name = e.target.name;
      value = e.target.value;
    } else if (e.name) {
      name = e.name;
      value = e.value;
    } else if (typeof e === 'object' && e instanceof Date) {
      name = 'date';
      value = e;
    }

    if (name) {
      if (name === 'date' || name === 'sectorType') {
        updateFilterState(name, value);
      } else {
        debouncedUpdateFilter(name, value);
      }
    }
  };

  function objectToUrlParams(obj) {
    if (!obj || typeof obj !== 'object') return '';
    return Object.keys(obj)
      .filter(key => obj[key] !== undefined && obj[key] !== null && String(obj[key]).trim() !== '')
      .map(key => {
        const val = obj[key];
        let formattedVal = String(val).trim();
        if (key === "date") {
          if (val instanceof Date) {
            formattedVal = formatDateToYYYYMMDD(val);
          } else if (typeof val === 'string' && val.includes('T')) {
            formattedVal = val.split('T')[0];
          }
        }
        return `${encodeURIComponent(key)}=${encodeURIComponent(formattedVal)}`;
      })
      .join('&');
  }

  // React state controlled Auto Fill calculations
  function calculateAutoFill(key, startDateIndex, startVal, dates, mealNumber, repeat, currentMatrix) {
    const newMatrix = { ...currentMatrix };
    const numDates = dates.length;

    // Auto fill down
    let incDown = 0;
    let valDown = +startVal;
    for (let i = startDateIndex; i < numDates; i++) {
      if (incDown === repeat) {
        valDown = valDown + 1;
        incDown = 0;
      }
      if (valDown > mealNumber) {
        valDown = 1;
      }
      const cellKey = `${dates[i]}_${key}`;
      newMatrix[cellKey] = valDown;
      incDown++;
    }

    // Auto fill up
    let incUp = 0;
    let valUp = +startVal - 1;
    for (let i = startDateIndex - 1; i >= 0; i--) {
      if (incUp === repeat) {
        valUp = valUp - 1;
        incUp = 0;
      }
      if (valUp < 1) {
        valUp = mealNumber;
      }
      const cellKey = `${dates[i]}_${key}`;
      newMatrix[cellKey] = valUp;
      incUp++;
    }

    return newMatrix;
  }

  function onChangeRotInp(e) {
    const key = e.target.dataset.key;
    const selectedDate = e.target.dataset.date;
    const index = parseInt(e.target.dataset.index, 10);
    const rawVal = e.target.value;

    if (rawVal === '') {
      setMatrixData(prev => {
        const next = { ...prev };
        delete next[`${selectedDate}_${key}`];
        return next;
      });
      return;
    }

    const value = +rawVal;
    if (isNaN(value) || value > rotationData.mealNumber || value <= 0) {
      toast.dismiss();
      toast.error(`Value should be between 1 and ${rotationData.mealNumber}`, {
        position: toast.POSITION.TOP_CENTER,
        hideProgressBar: true,
        pauseOnHover: true,
        draggable: false,
        autoClose: 3000,
        style: {
          backgroundColor: '#d9534f',
          color: '#fff',
          borderRadius: '8px',
          fontSize: '1rem',
          width: '30rem'
        }
      });
      return;
    }

    setRDataExist(true);
    const updatedMatrix = calculateAutoFill(
      key,
      index,
      value,
      rotationCreateDates,
      rotationData.mealNumber,
      rotationData.repeat,
      matrixData
    );
    setMatrixData(updatedMatrix);
  }

  function rotationDataChange(e) {
    const { name, value } = e.target;
    if (+value < 1) return;
    if (rDataExist) {
      const confirm = window.confirm('This will reset all inputs. Do you want to continue?');
      if (confirm) {
        setRotationData(prev => ({ ...prev, [name]: +value }));
        resetRotationData();
      }
    } else {
      setRotationData(prev => ({ ...prev, [name]: +value }));
    }
  }

  function resetRotationData() {
    setMatrixData({});
    setRDataExist(false);
  }

  const handleSaveMatrix = async () => {
    if (isSubmitting) return;

    const entries = Object.entries(matrixData);
    if (entries.length === 0) {
      toast.error("Please enter menu rotation values before saving.");
      return;
    }

    const payload = [];
    rotationCreateDates.forEach((date) => {
      keyArr.forEach((key, kIdx) => {
        const val = matrixData[`${date}_${key}`];
        if (val !== undefined && val !== null && val !== '') {
          payload.push({
            key: key,
            date: date,
            warehouse: wHArry[kIdx],
            caterer: catererArr[kIdx],
            menuId: String(val)
          });
        }
      });
    });

    if (payload.length === 0) {
      toast.error("No valid rotation records to save.");
      return;
    }

    setIsSubmitting(true);
    try {
      const resp = await DFPAPIEndpointGalley.post("galley/ai/menu-rotation/configs", { menuRotationPolicy: payload });
      if (resp && resp.status === 1) {
        const createdCount = resp.createdCount ?? 0;
        const skippedCount = resp.skippedCount ?? 0;
        if (createdCount > 0 && skippedCount === 0) {
          toast.success(`Successfully created ${createdCount} menu rotation record(s).`);
        } else if (createdCount > 0 && skippedCount > 0) {
          toast.warning(`Created ${createdCount} record(s), but ${skippedCount} duplicate(s) were skipped.`);
        } else if (createdCount === 0 && skippedCount > 0) {
          toast.error(`No records created. ${skippedCount} duplicate(s) were skipped.`);
        } else {
          toast.info(resp.message || "Menu rotation submitted.");
        }
        setCreateMenuRotation(false);
        resetRotationData();
        setReloadCounter(c => c + 1);
      } else {
        toast.error(resp?.message || "Failed to create menu rotation.");
      }
    } catch (error) {
      let errMsg = "Failed to save menu rotation records.";
      if (error?.message) {
        try {
          const parsed = JSON.parse(error.message);
          errMsg = parsed?.data?.message || parsed?.data?.error || errMsg;
        } catch (e) {
          errMsg = error.message;
        }
      }
      toast.error(errMsg);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEditChange = (e) => {
    const { name, value } = e.target;
    setEditForm(prev => ({ ...prev, [name]: value }));
  };

  const handleSaveEdit = async (e, updated) => {
    if (isEditSubmitting) return;

    const currentForm = updated ? { ...editForm, ...updated } : editForm;

    let dateVal = currentForm.date;
    if (dateVal instanceof Date) {
      dateVal = formatDateToYYYYMMDD(dateVal);
    } else if (typeof dateVal === 'string' && dateVal.includes('T')) {
      dateVal = dateVal.split('T')[0];
    }

    if (!currentForm.key || !dateVal || !currentForm.warehouse || !currentForm.caterer || !currentForm.menuid) {
      toast.error("All fields are required for editing.");
      return;
    }

    const numVal = Number(currentForm.menuid);
    if (isNaN(numVal) || numVal < 1) {
      toast.error("Menu ID must be a valid number.");
      return;
    }

    setIsEditSubmitting(true);
    try {
      const payload = {
        key: currentForm.key,
        date: dateVal,
        warehouse: currentForm.warehouse,
        caterer: currentForm.caterer,
        menuId: String(currentForm.menuid)
      };

      if (!currentForm.id) {
        toast.error("Record ID is missing. Cannot update.");
        setIsEditSubmitting(false);
        return;
      }
      
      const resp = await DFPAPIEndpointGalley.put(`galley/ai/menu-rotation/configs/${currentForm.id}`, payload);

      if (resp && resp.status === 1) {
        toast.success("Menu rotation record updated successfully!");
        setEditModalOpen(false);
        setReloadCounter(c => c + 1);
      } else {
        toast.error(resp?.message || "Failed to update record.");
      }
    } catch (error) {
      let errMsg = "Failed to update menu rotation record.";
      if (error?.message) {
        try {
          const parsed = JSON.parse(error.message);
          errMsg = parsed?.data?.message || parsed?.data?.error || errMsg;
        } catch (e) {
          errMsg = error.message;
        }
      }
      toast.error(errMsg);
    } finally {
      setIsEditSubmitting(false);
    }
  };

  if (createMenuRotation) {
    return (
      <div className="op-aircraft-container flexCol full-flex ag-theme-alpine-dark" style={{ width: "100%", maxWidth: "100%", minWidth: 0, overflowX: "hidden", boxSizing: "border-box" }}>
        <div className='flexRow width100 margTop8 margBot10 alignItemsCenter' style={{ width: "100%", maxWidth: "100%", boxSizing: "border-box", display: "flex", justifyContent: "space-between", flexShrink: 0 , paddingRight:"215px"}}>
          <div style={{ display: "flex", alignItems: "center", gap: "20px" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
              <label htmlFor="mealNumber" style={{ fontWeight: "600", fontSize: "0.88rem", color: "#cbd5e0", whiteSpace: "nowrap", margin: 0, padding: 0, lineHeight: "1" }}>
                Number of Menu
              </label>
              <input
                className='edit-input'
                style={{ width: "70px", height: "36px", textAlign: "center", borderRadius: "6px", border: "1px solid #2d3748", backgroundColor: "#1a202c", color: "#fff", fontWeight: "600", margin: 0 }}
                onChange={rotationDataChange}
                name='mealNumber'
                type="number"
                value={rotationData.mealNumber}
              />
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
              <label htmlFor="repeat" style={{ fontWeight: "600", fontSize: "0.88rem", color: "#cbd5e0", whiteSpace: "nowrap", margin: 0, padding: 0, lineHeight: "1" }}>
                Repeat
              </label>
              <input
                className='edit-input'
                style={{ width: "70px", height: "36px", textAlign: "center", borderRadius: "6px", border: "1px solid #2d3748", backgroundColor: "#1a202c", color: "#fff", fontWeight: "600", margin: 0 }}
                onChange={rotationDataChange}
                name='repeat'
                type="number"
                value={rotationData.repeat}
              />
            </div>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: "10px", marginLeft: "auto", flexShrink: 0 }}>
            <button
              className="add-item-button-all"
              style={{ height: "36px", padding: "0 18px", borderRadius: "6px", fontWeight: "600", cursor: "pointer", whiteSpace: "nowrap", flexShrink: 0 }}
              onClick={() => {
                setCreateMenuRotation(false);
                resetRotationData();
              }}
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              className="add-item-button"
              style={{ height: "36px", padding: "0 22px", borderRadius: "6px", fontWeight: "600", cursor: "pointer", whiteSpace: "nowrap", flexShrink: 0 }}
              onClick={handleSaveMatrix}
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Saving...' : 'Done'}
            </button>
          </div>
        </div>
        <div className='margBot10 margTop8' style={{ overflow: "auto", maxHeight: "calc(100vh - 180px)", width: "100%", maxWidth: "100%", minWidth: 0, boxSizing: "border-box", flex: "1 1 auto", borderRadius: "8px", border: "1px solid #2d3648", backgroundColor: "#0d111a" }}>
          <table className='mealRotationTable' style={{ borderCollapse: "separate", borderSpacing: 0, width: "max-content", minWidth: "100%", tableLayout: "fixed" }}>
            <thead style={{ position: "sticky", top: 0, zIndex: 20, backgroundColor: "#161b2a" }}>
              {/* Row 1: Key Header */}
              <tr>
                <th style={{ position: "sticky", left: 0, zIndex: 30, width: "120px", minWidth: "120px", backgroundColor: "#111625", borderBottom: "1px solid #2d3648", borderRight: "2px solid #4a5568", padding: "8px", textAlign: "center", color: "#63b3ed", fontSize: "0.8rem", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                  Key
                </th>
                {keyArr.map((key) => (
                  <th key={key} style={{ width: "85px", minWidth: "85px", backgroundColor: "#161b2a", borderBottom: "1px solid #2d3648", borderRight: "1px solid #2d3648", padding: "8px 4px", textAlign: "center", color: "#fff", fontSize: "0.8rem", fontWeight: "700" }} title={key}>
                    {key}
                  </th>
                ))}
              </tr>

              {/* Row 2: Warehouse Header */}
              <tr>
                <th style={{ position: "sticky", left: 0, zIndex: 30, width: "120px", minWidth: "120px", backgroundColor: "#111625", borderBottom: "1px solid #2d3648", borderRight: "2px solid #4a5568", padding: "6px 8px", textAlign: "center", color: "#a0aec0", fontSize: "0.78rem", fontWeight: "600" }}>
                  Warehouse
                </th>
                {wHArry.map((wh, idx) => (
                  <th key={idx} style={{ width: "85px", minWidth: "85px", backgroundColor: "#161b2a", borderBottom: "1px solid #2d3648", borderRight: "1px solid #2d3648", padding: "6px 4px", textAlign: "center", color: "#cbd5e0", fontSize: "0.78rem" }}>
                    {wh}
                  </th>
                ))}
              </tr>

              {/* Row 3: Caterer Header */}
              <tr>
                <th style={{ position: "sticky", left: 0, zIndex: 30, width: "120px", minWidth: "120px", backgroundColor: "#111625", borderBottom: "2px solid #4a5568", borderRight: "2px solid #4a5568", padding: "6px 8px", textAlign: "center", color: "#a0aec0", fontSize: "0.78rem", fontWeight: "600" }}>
                  Caterer
                </th>
                {catererArr.map((cr, idx) => (
                  <th key={idx} style={{ width: "85px", minWidth: "85px", backgroundColor: "#161b2a", borderBottom: "2px solid #4a5568", borderRight: "1px solid #2d3648", padding: "6px 4px", textAlign: "center", color: "#cbd5e0", fontSize: "0.78rem" }}>
                    {cr}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rotationCreateDates.map((date, i) => {
                const displayDate = date.split('-').reverse().join('/');
                return (
                  <tr key={date} style={{ backgroundColor: i % 2 === 0 ? "#141824" : "#0f131f" }}>
                    {/* Sticky Date Cell */}
                    <td style={{ position: "sticky", left: 0, zIndex: 10, width: "120px", minWidth: "120px", backgroundColor: i % 2 === 0 ? "#141824" : "#0f131f", borderRight: "2px solid #4a5568", borderBottom: "1px solid #2d3648", padding: "8px 10px", whiteSpace: "nowrap", fontSize: "0.85rem", fontWeight: "600", color: "#e2e8f0", textAlign: "center" }}>
                      {displayDate}
                    </td>
                    {keyArr.map((key) => {
                      const cellVal = matrixData[`${date}_${key}`] !== undefined ? matrixData[`${date}_${key}`] : '';
                      return (
                        <td style={{ width: "85px", minWidth: "85px", borderRight: "1px solid #2d3648", borderBottom: "1px solid #2d3648", padding: "6px 4px", textAlign: "center", verticalAlign: "middle" }} key={key}>
                          <input
                            onChange={onChangeRotInp}
                            data-index={i}
                            id={`${key}-${i}`}
                            type="text"
                            data-key={key}
                            data-date={date}
                            value={cellVal}
                            className={`edit-input rotationDataInp ${key}-${i}`}
                            style={{ width: "48px", height: "32px", margin: "0 auto", display: "block", textAlign: "center", borderRadius: "6px", border: "1px solid #2d3748", backgroundColor: cellVal ? "#1a365d" : "#1a202c", color: "#fff", fontWeight: "700", fontSize: "0.9rem", outline: "none" }}
                          />
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  return (
    <div className="op-aircraft-container flexCol full-flex ag-theme-alpine-dark" style={{ width: "100%", maxWidth: "100%", minWidth: 0, overflowX: "hidden", boxSizing: "border-box" }}>
      <div className='flexRow width100 margBot10 margTop8 alignItemsCenter' style={{ boxSizing: "border-box", display: "flex", justifyContent: "space-between", flexWrap: "nowrap", gap: "12px", width: "100%", maxWidth: "100%", flexShrink: 0 }}>
        <div className='flexRow alignItemsCenter' style={{ flexWrap: "wrap", gap: "10px", flex: "1 1 auto", minWidth: 0 }}>
          {/* filters */}
          <div style={{ minWidth: "140px", width: "140px", marginRight: "6px", flexShrink: 0 }}>
            <select
              onChange={filterChange}
              className="select-item darkBlue-bg"
              id="sectorType"
              name='sectorType'
              style={{ width: "100%", minWidth: 0, maxWidth: "100%", margin: 0, boxSizing: "border-box" }}
            >
              <option value="">Sector Type</option>
              <option value="int">Int</option>
              <option value="dom">Dom</option>
              <option value="vip">VIP</option>
            </select>
          </div>
          <div style={{ minWidth: "110px", width: "110px", marginRight: "6px", flexShrink: 0 }}>
            <input
              onChange={filterChange}
              className='edit-input'
              name='key'
              type="text"
              placeholder='Key'
              style={{ width: "100%", margin: 0, boxSizing: "border-box" }}
            />
          </div>
          <div style={{ minWidth: "115px", width: "115px", marginRight: "6px", flexShrink: 0 }}>
            <input
              onChange={filterChange}
              className='edit-input'
              name='catererCode'
              type="text"
              placeholder='Caterer'
              style={{ width: "100%", margin: 0, boxSizing: "border-box" }}
            />
          </div>
          <div style={{ minWidth: "120px", width: "120px", marginRight: "6px", flexShrink: 0 }}>
            <input
              onChange={filterChange}
              className='edit-input'
              name='warehouseCode'
              type="text"
              placeholder='Warehouse'
              style={{ width: "100%", margin: 0, boxSizing: "border-box" }}
            />
          </div>
          <div style={{ minWidth: "105px", width: "105px", marginRight: "6px", flexShrink: 0 }}>
            <input
              onChange={filterChange}
              className='edit-input'
              name='menuId'
              type="text"
              placeholder='Menu ID'
              style={{ width: "100%", margin: 0, boxSizing: "border-box" }}
            />
          </div>
          <div style={{ minWidth: "135px", width: "135px", marginRight: "6px", flexShrink: 0 }}>
            <NGODatePicker
              className="orderByDateInp"
              value={filters?.date || null}
              name="date"
              placeholder={"Date"}
              onChange={filterChange}
              dateformat="dd-mm-yyyy"
            />
          </div>
          {/* filters */}
        </div>
        {createMealRotation ? (
          <div style={{ marginLeft: "auto", flexShrink: 0 }}>
            <button onClick={() => setCreateMenuRotation(!createMenuRotation)} className="add-item-button" style={{ whiteSpace: "nowrap" }}>+ Create</button>
          </div>
        ) : <></>}
      </div>

      <DataGrid
        className="full-flex"
        columns={Columns}
        initialPageLimit={50}
        showDefaultFilters={false}
        actionProperties={actionColumnProperties}
        noResultsMessage={"No Data"}
        rowSelection="multiple"
        columnResize={true}
        actions={actionItems}
        getActionCellIcons={getActionCellIcons}
        onAction={onAction}
        getRows={fetchMealRotationData}
        resetDataSource={reloadCounter}
      />
      {!ready && <div className='overlay_hide sub'></div>}

      {/* Edit Modal */}
      {editModalOpen && (
        <EditModal
          title="Edit Menu Rotation"
          onClose={() => setEditModalOpen(false)}
          onSubmit={handleSaveEdit}
          onChange={(e, updated) => {
            if (updated) {
              setEditForm(prev => ({ ...prev, ...updated }));
            }
          }}
          buttonLabel="Save Changes"
          showFieldsInColumn={true}
          fields={[
            { label: 'Key', attribute: 'key', type: 'text', required: true, initialValue: editForm.key },
            { label: 'Date', attribute: 'date', type: 'date', required: true, initialValue: editForm.date ? new Date(editForm.date) : null },
            { label: 'Warehouse', attribute: 'warehouse', type: 'text', required: true, initialValue: editForm.warehouse },
            { label: 'Caterer', attribute: 'caterer', type: 'text', required: true, initialValue: editForm.caterer },
            { label: 'Menu ID', attribute: 'menuid', type: 'text', required: true, initialValue: editForm.menuid }
          ]}
        />
      )}
    </div>
  );
};